import React, { FunctionComponent, memo, useCallback } from "react";
import { Grid, Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Helmet } from "react-helmet";
import { Input, Singleselect, ListOptionBase } from "@shared/components";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import { FIELD_TITLES } from "@investorRequest/constants/fieldTooltips";
import InvestorRequestTextarea from "@investorRequest/components/InvestorRequestTextarea";
import InvestorRequestDatepicker from "@investorRequest/components/InvestorRequestDatepicker";
import FurtherCreditFields from "@investorRequest/components/FurtherCreditFields";
import IntermediaryFields from "@investorRequest/components/IntermediaryFields";
import { validationMessages } from "@investorRequest/constants/validationMessages";
import { fieldKeys } from "@investorRequest/constants/fieldKeys";
import { useDropdownOverflowTooltip } from "@shared/hooks/useDropdownOverflowTooltip";
import { getDropdownWithIconStyles } from "@shared/styles/dropdownWithIconStyles";
import { Nullable } from "@shared/models/general";
import {
  sanitizeAsciiName,
  isValidAccountNumber,
  sanitizeAccountNumberInput,
  sanitizeSwiftId,
  sanitizeIBAN,
  sanitizeBankId,
  sanitizeBranchId,
  isValidSwiftId,
  isValidIBAN,
  isValidBankId,
  isValidBranchId,
} from "@investorRequest/utils/SpecificValidations";
import { useC2DSErrors } from "@investorRequest/hooks/useC2DSFieldError";

const MAX_BENEFICIARY_ACCOUNT_NAME = 140;
const MAX_BANK_NAME = 30;
const MAX_BANK_ACCOUNT_NUMBER = 35;
// IR-UI-Revamp_V1: SWIFT field max lengths
const MAX_SWIFT_ID = 11;
const MAX_IBAN = 34;
const MAX_BANK_ID = 20;
const MAX_BRANCH_ID = 10;

const useStyles = makeStyles((theme) => getDropdownWithIconStyles(theme));

interface Props {
  values: {
    bankName: string;
    beneficiaryAccountName: string;
    bankCountry: string;
    bankAccountNumber: string;
    intermediaryYesNo?: string;
    intermediarySwiftOrAba?: string;
    intermediaryBankName?: string;
    intermediaryRoutingNumber?: string;
    intermediaryPaymentMessage?: string;
    furtherCreditYesNo?: string;
    furtherCreditLine1?: string;
    furtherCreditLine2?: string;
    // IR-UI-Revamp_V1: New SWIFT fields
    SwiftId?: string;
    IBAN?: string;
    BankIdNbr?: string;
    BranchId?: string;
    effectiveDate?: string;
  };
  countriesOptions: ListOptionBase[];
  selectedCountry?: ListOptionBase;
  onSelectCountry(option: ListOptionBase): void;
  onChange(field: string, value: string): void;
  showErrors: boolean;
  inputXS: 12 | 6;
  disableBankCountry?: boolean;
}

const InvestorRequestSwiftFields: FunctionComponent<Props> = ({
  values,
  onChange,
  showErrors,
  inputXS,
  countriesOptions,
  selectedCountry,
  onSelectCountry,
  disableBankCountry = false,
}) => {
  const classes = useStyles();
  const { selectedValueRef, dropdownOpen, isToggleOverflowing, selectKey, handleSelect, handleToggleButtonClick } =
    useDropdownOverflowTooltip({
      testId: "beneficiary-bank-country-select",
      selectedLabel: selectedCountry?.label,
    });

  // LPC-10289: C2DS validation errors
  const { hasFieldError, getFieldErrorMessage, clearFieldError } = useC2DSErrors();

  // LPC-10289: Cache C2DS error states to ensure consistency between error prop and message display
  const hasSwiftIdC2DSError = hasFieldError("SwiftId");
  const hasIBANC2DSError = hasFieldError("IBAN");
  const hasBankIdC2DSError = hasFieldError("BankIdNbr");

  const handleDatepickerChange = useCallback(
    (value: Nullable<Date>) => onChange("effectiveDate", value ? value.toISOString() : ""),
    [onChange]
  );

  return (
    <>
      <Helmet>
        <link rel="stylesheet" href="/assets/auth/css/wireformstyle.css" />
      </Helmet>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BeneficiaryAccountName}>
          <Input
            dense={false}
            placeholder="*Beneficiary Account Name"
            variant="light"
            size="large"
            value={values.beneficiaryAccountName}
            onChange={(v) =>
              onChange(fieldKeys.BeneficiaryAccountName, sanitizeAsciiName(v, MAX_BENEFICIARY_ACCOUNT_NAME))
            }
            sanitizeOnPaste={(t) => sanitizeAsciiName(t, MAX_BENEFICIARY_ACCOUNT_NAME)}
            error={showErrors && !(values.beneficiaryAccountName || "").trim()}
          />
        </FieldTooltip>
        {showErrors && !(values.beneficiaryAccountName || "").trim() && (
          <Box className="ir-helper-text-warning">{validationMessages.beneficiaryAccountName.required}</Box>
        )}
        {showErrors &&
          !!(values.beneficiaryAccountName || "").trim() &&
          values.beneficiaryAccountName.length > MAX_BENEFICIARY_ACCOUNT_NAME && (
            <Box className="ir-helper-text-warning">Max 140 characters</Box>
          )}
        {showErrors &&
          !!(values.beneficiaryAccountName || "").trim() &&
          values.beneficiaryAccountName.length <= MAX_BENEFICIARY_ACCOUNT_NAME &&
          /[^A-Za-z0-9\s]/.test(values.beneficiaryAccountName) && (
            <Box className="ir-helper-text-warning">{validationMessages.beneficiaryAccountName.invalid}</Box>
          )}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BankName}>
          <Input
            dense={false}
            placeholder="*Beneficiary Bank Name"
            variant="light"
            size="large"
            value={values.bankName}
            onChange={(v) => onChange(fieldKeys.BankName, sanitizeAsciiName(v, MAX_BANK_NAME))}
            sanitizeOnPaste={(t) => sanitizeAsciiName(t, MAX_BANK_NAME)}
            error={showErrors && !(values.bankName || "").trim()}
          />
        </FieldTooltip>
        {showErrors && !(values.bankName || "").trim() && (
          <Box className="ir-helper-text-warning">{validationMessages.bankName.required}</Box>
        )}
        {showErrors && !!(values.bankName || "").trim() && values.bankName.length > MAX_BANK_NAME && (
          <Box className="ir-helper-text-warning">Max 30 characters</Box>
        )}
        {showErrors &&
          !!(values.bankName || "").trim() &&
          values.bankName.length <= MAX_BANK_NAME &&
          /[^A-Za-z0-9\s]/.test(values.bankName) && (
            <Box className="ir-helper-text-warning">{validationMessages.bankName.invalid}</Box>
          )}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip
          titleNode={
            <Box>
              <Typography>Beneficiary Bank Country</Typography>
              {isToggleOverflowing && selectedCountry?.label ? <Typography>{selectedCountry.label}</Typography> : null}
            </Box>
          }
          disableHover={dropdownOpen}
        >
          <Box className={classes.bankCountryContainer}>
            <Box className={classes.bankCountryDropdown} ref={selectedValueRef}>
              <Box>
                <Singleselect
                  key={selectKey}
                  options={countriesOptions}
                  selectedOption={selectedCountry}
                  onSelect={(option) => {
                    onSelectCountry(option);
                    handleSelect();
                  }}
                  onToggleButtonClick={handleToggleButtonClick}
                  label={selectedCountry?.label || "*Beneficiary Bank Country"}
                  variant="contained"
                  size="large"
                  withSearch
                  testId="beneficiary-bank-country-select"
                  disabled={disableBankCountry}
                />
              </Box>
            </Box>
          </Box>
        </FieldTooltip>
        {showErrors && !selectedCountry && (
          <Box className="ir-helper-text-warning">{validationMessages.beneficiaryBankCountry.required}</Box>
        )}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BeneficiaryBankAccountNumber}>
          <Input
            dense={false}
            placeholder="*Beneficiary Bank Account #"
            variant="light"
            size="large"
            value={values.bankAccountNumber}
            onChange={(v) =>
              onChange(fieldKeys.BankAccountNumber, sanitizeAccountNumberInput(v, MAX_BANK_ACCOUNT_NUMBER, 10))
            }
            sanitizeOnPaste={(t) => sanitizeAccountNumberInput(t, MAX_BANK_ACCOUNT_NUMBER, 10)}
            error={showErrors && (!values.bankAccountNumber || !isValidAccountNumber(values.bankAccountNumber))}
          />
        </FieldTooltip>
        {showErrors &&
          (!values.bankAccountNumber ? (
            <Box className="ir-helper-text-warning">{validationMessages.bankAccountNumber.required}</Box>
          ) : !isValidAccountNumber(values.bankAccountNumber) ? (
            <Box className="ir-helper-text-warning">{validationMessages.bankAccountNumber.invalid}</Box>
          ) : null)}
        {showErrors && !!values.bankAccountNumber && values.bankAccountNumber.length > MAX_BANK_ACCOUNT_NUMBER && (
          <Box className="ir-helper-text-warning">Max 35 characters</Box>
        )}
      </Grid>
      {/* IR-UI-Revamp_V1: New SWIFT fields */}
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.Swift}>
          <Input
            dense={false}
            placeholder="*SWIFT"
            variant="light"
            size="large"
            value={values.SwiftId || ""}
            onChange={(v) => {
              // LPC-10289: Clear C2DS error when user modifies field
              clearFieldError("SwiftId");
              onChange(fieldKeys.SwiftId, sanitizeSwiftId(v));
            }}
            sanitizeOnPaste={(t) => sanitizeSwiftId(t)}
            error={(showErrors && (!values.SwiftId || !isValidSwiftId(values.SwiftId))) || hasSwiftIdC2DSError}
          />
        </FieldTooltip>
        {showErrors && !values.SwiftId && (
          <Box className="ir-helper-text-warning">{validationMessages.swiftId.required}</Box>
        )}
        {showErrors && !!values.SwiftId && values.SwiftId.length > MAX_SWIFT_ID && (
          <Box className="ir-helper-text-warning">Max 11 characters</Box>
        )}
        {showErrors && !!values.SwiftId && values.SwiftId.length <= MAX_SWIFT_ID && !isValidSwiftId(values.SwiftId) && (
          <Box className="ir-helper-text-warning">{validationMessages.swiftId.invalid}</Box>
        )}
        {/* LPC-10289: C2DS validation error */}
        {hasSwiftIdC2DSError && <Box className="ir-helper-text-warning">{getFieldErrorMessage("SwiftId")}</Box>}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.Iban}>
          <Input
            dense={false}
            placeholder="IBAN"
            variant="light"
            size="large"
            value={values.IBAN || ""}
            onChange={(v) => {
              // LPC-10289: Clear C2DS error when user modifies field
              clearFieldError("IBAN");
              onChange(fieldKeys.IBAN, sanitizeIBAN(v));
            }}
            sanitizeOnPaste={(t) => sanitizeIBAN(t)}
            error={(showErrors && !!values.IBAN && !isValidIBAN(values.IBAN)) || hasIBANC2DSError}
          />
        </FieldTooltip>
        {showErrors && !!values.IBAN && values.IBAN.length > MAX_IBAN && (
          <Box className="ir-helper-text-warning">Max 34 characters</Box>
        )}
        {showErrors && !!values.IBAN && values.IBAN.length <= MAX_IBAN && !isValidIBAN(values.IBAN) && (
          <Box className="ir-helper-text-warning">{validationMessages.iban.invalid}</Box>
        )}
        {/* LPC-10289: C2DS validation error */}
        {hasIBANC2DSError && <Box className="ir-helper-text-warning">{getFieldErrorMessage("IBAN")}</Box>}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BankId}>
          <Input
            dense={false}
            placeholder="Bank ID"
            variant="light"
            size="large"
            value={values.BankIdNbr || ""}
            onChange={(v) => {
              clearFieldError("BankIdNbr");
              onChange(fieldKeys.BankIdNbr, sanitizeBankId(v));
            }}
            sanitizeOnPaste={(t) => sanitizeBankId(t)}
            error={(showErrors && !!values.BankIdNbr && !isValidBankId(values.BankIdNbr)) || hasBankIdC2DSError}
          />
        </FieldTooltip>
        {showErrors && !!values.BankIdNbr && values.BankIdNbr.length > MAX_BANK_ID && (
          <Box className="ir-helper-text-warning">Max 20 characters</Box>
        )}
        {showErrors &&
          !!values.BankIdNbr &&
          values.BankIdNbr.length <= MAX_BANK_ID &&
          !isValidBankId(values.BankIdNbr) && (
            <Box className="ir-helper-text-warning">{validationMessages.bankId.invalid}</Box>
          )}
        {hasBankIdC2DSError && <Box className="ir-helper-text-warning">{getFieldErrorMessage("BankIdNbr")}</Box>}
      </Grid>
      <Grid item xs={inputXS}>
        <FieldTooltip title={FIELD_TITLES.BranchId}>
          <Input
            dense={false}
            placeholder="Branch ID"
            variant="light"
            size="large"
            value={values.BranchId || ""}
            onChange={(v) => onChange(fieldKeys.BranchId, sanitizeBranchId(v))}
            sanitizeOnPaste={(t) => sanitizeBranchId(t)}
            error={showErrors && !!values.BranchId && !isValidBranchId(values.BranchId)}
          />
        </FieldTooltip>
        {showErrors && !!values.BranchId && values.BranchId.length > MAX_BRANCH_ID && (
          <Box className="ir-helper-text-warning">Max 10 characters</Box>
        )}
        {showErrors &&
          !!values.BranchId &&
          values.BranchId.length <= MAX_BRANCH_ID &&
          !isValidBranchId(values.BranchId) && (
            <Box className="ir-helper-text-warning">{validationMessages.branchId.invalid}</Box>
          )}
      </Grid>
      <IntermediaryFields
        yesNoValue={values.intermediaryYesNo || ""}
        swiftOrAba={values.intermediarySwiftOrAba || ""}
        bankName={values.intermediaryBankName || ""}
        routingNumber={values.intermediaryRoutingNumber || ""}
        paymentMessage={values.intermediaryPaymentMessage || ""}
        onChangeYesNo={(v) => {
          onChange("intermediaryYesNo", v);
          // IR-UI-Revamp_V1: Clear intermediary fields when "No" is selected
          if (v === "no") {
            onChange("intermediarySwiftOrAba", "");
            onChange("intermediaryBankName", "");
            onChange("intermediaryRoutingNumber", "");
            onChange("intermediaryPaymentMessage", "");
          }
        }}
        onChangeSwiftOrAba={(v) => onChange("intermediarySwiftOrAba", v)}
        onChangeBankName={(v) => onChange("intermediaryBankName", v)}
        onChangeRoutingNumber={(v) => onChange("intermediaryRoutingNumber", v)}
        onChangePaymentMessage={(v) => onChange("intermediaryPaymentMessage", v)}
        showErrors={showErrors}
      />
      <FurtherCreditFields
        yesNoValue={values.furtherCreditYesNo || ""}
        line1={values.furtherCreditLine1 || ""}
        line2={values.furtherCreditLine2 || ""}
        onChangeYesNo={(v) => {
          onChange("furtherCreditYesNo", v);
          // IR-UI-Revamp_V1: Clear further credit fields when "No" is selected
          if (v === "no") {
            onChange("furtherCreditLine1", "");
            onChange("furtherCreditLine2", "");
          }
        }}
        onChangeLine1={(v) => onChange("furtherCreditLine1", v)}
        onChangeLine2={(v) => onChange("furtherCreditLine2", v)}
        showErrors={showErrors}
      />
      <Grid item xs={inputXS} data-testid="investor-request-datepicker">
        <FieldTooltip title={FIELD_TITLES.DateInstructionsBecomeEffective}>
          <InvestorRequestDatepicker value={values.effectiveDate || ""} onChange={handleDatepickerChange} />
        </FieldTooltip>
      </Grid>
      <Grid item xs={12}>
        <FieldTooltip title={FIELD_TITLES.AdditionalNote}>
          <InvestorRequestTextarea
            placeholder="Please include any additional notes"
            value={(values as any).freeText || ""}
            onChange={(v: string) => onChange("freeText", v)}
            error={showErrors && ((values as any).freeText || "").length > 8000}
          />
        </FieldTooltip>
        {showErrors && !!(values as any).freeText && ((values as any).freeText as string).length > 8000 && (
          <Box mt={1} color="warning.main" fontSize={12}>
            Max 8000 characters
          </Box>
        )}
      </Grid>
    </>
  );
};

export default memo(InvestorRequestSwiftFields);
